'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnrampClient } from '@morfi/onramp-sdk';
import { type FundingCheckout, type HostedWidgetProvider, useFundingHostedWidgetUrl } from '@morfi/onramp-sdk/react';
import { mountSignedFundingWidget, refreshHostedConnection, type WidgetEvent } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { FundingProviderLoading } from '../../shared/funding-provider-loading';
export function FundingHostedWidget(t0) {
  const $ = _c(29);
  const {
    provider,
    client,
    checkout
  } = t0;
  const container = useRef(null);
  const t = useTranslations("onramp");
  const [ready, setReady] = useState(false);
  const {
    paymentSignature,
    attemptId
  } = checkout;
  const signature = checkout.quote?.signature === paymentSignature ? paymentSignature : null;
  const signed = useFundingHostedWidgetUrl(client, provider, checkout.operation?.id, attemptId, signature);
  let t1;
  if ($[0] !== checkout || $[1] !== t) {
    t1 = () => {
      logger.warn("onramp.hosted_widget_setup_failed", {
        method: checkout.method,
        surface: "web",
        code: "widget_unavailable"
      });
      showToast({
        id: TOAST_IDS.ONRAMP_PAYMENT_PAGE_LOAD,
        message: t("payment_page_load_failed"),
        variant: "error"
      });
      checkout.paymentPageLoadFailed();
    };
    $[0] = checkout;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const reportError = useEffectEvent(t1);
  let t2;
  if ($[3] !== checkout || $[4] !== ready || $[5] !== reportError) {
    t2 = event => {
      if (event.kind === "error" && !ready) {
        reportError();
        return;
      }
      if (event.kind === "ready" || event.kind === "error") {
        setReady(true);
      }
      if (event.kind === "error") {
        logger.warn("onramp.hosted_widget_event_failed", {
          method: checkout.method,
          surface: "web",
          phase: "provider_failed",
          code: event.payload.code,
          attemptId: checkout.attemptId,
          hasProviderTransaction: Boolean(checkout.transactionId)
        });
      }
      checkout.hostedEvent(event);
    };
    $[3] = checkout;
    $[4] = ready;
    $[5] = reportError;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onEvent = useEffectEvent(t2);
  let t3;
  if ($[7] !== reportError || $[8] !== signed.isError) {
    t3 = () => {
      if (signed.isError) {
        reportError();
      }
    };
    $[7] = reportError;
    $[8] = signed.isError;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== signed.isError) {
    t4 = [signed.isError];
    $[10] = signed.isError;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  const url = signed.data?.url;
  let t5;
  if ($[12] !== checkout.method || $[13] !== onEvent || $[14] !== provider || $[15] !== reportError || $[16] !== url) {
    t5 = () => {
      if (!container.current || !url) {
        return;
      }
      const owned = document.createElement("div");
      owned.className = "h-full w-full";
      container.current.append(owned);
      let active = true;
      const loadTimer = setTimeout(() => {
        if (!active) {
          return;
        }
        active = false;
        owned.remove();
        frame?.dispose();
        reportError();
      }, 20000);
      let frame;
      let events = Promise.resolve();
      try {
        frame = mountSignedFundingWidget({
          container: owned,
          url,
          onEvent(event_0) {
            if (!active) {
              return;
            }
            clearTimeout(loadTimer);
            events = events.then(async () => {
              if (!active) {
                return;
              }
              if (event_0.kind === "complete" || event_0.kind === "close") {
                if (!(await refreshHostedConnection(provider))) {
                  logger.warn("onramp.hosted_widget_connection_refresh_failed", {
                    method: checkout.method,
                    surface: "web"
                  });
                }
              }
              if (active) {
                onEvent(event_0);
              }
            });
          }
        });
      } catch {
        clearTimeout(loadTimer);
        active = false;
        owned.remove();
        reportError();
      }
      return () => {
        active = false;
        clearTimeout(loadTimer);
        owned.remove();
        frame?.dispose();
      };
    };
    $[12] = checkout.method;
    $[13] = onEvent;
    $[14] = provider;
    $[15] = reportError;
    $[16] = url;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== checkout.method || $[19] !== provider || $[20] !== url) {
    t6 = [provider, url, checkout.method];
    $[18] = checkout.method;
    $[19] = provider;
    $[20] = url;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  if ($[22] !== ready) {
    t7 = !ready ? <div className="absolute inset-0"><FundingProviderLoading /></div> : null;
    $[22] = ready;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = ready ? "h-full w-full" : "h-full w-full opacity-0";
  let t9;
  if ($[24] !== t8) {
    t9 = <div ref={container} className={t8} />;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t7 || $[27] !== t9) {
    t10 = <div className="relative h-145 w-full">{t7}{t9}</div>;
    $[26] = t7;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}